import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useScrollTrigger,
  Container,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { landingColors } from '../../../theme/landingTheme';

interface NavbarProps {
  onRequestDemo: () => void;
}

const navItems = [
  { label: 'Features', href: '#features' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'Testimonials', href: '#testimonials' },
];

export const Navbar: React.FC<NavbarProps> = ({ onRequestDemo }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 50,
  });

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileOpen(false);
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: trigger ? 'rgba(255, 255, 255, 0.95)' : 'transparent',
          backdropFilter: trigger ? 'blur(20px)' : 'none',
          borderBottom: trigger ? `1px solid ${landingColors.border}` : 'none',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar
            sx={{
              justifyContent: 'space-between',
              py: { xs: 1, md: trigger ? 0.5 : 1 },
              transition: 'padding 0.3s ease',
            }}
          >
            {/* Logo */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                cursor: 'pointer',
              }}
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '10px',
                  background: `linear-gradient(135deg, ${landingColors.primary} 0%, ${landingColors.secondary} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    color: '#fff',
                    fontWeight: 800,
                    fontSize: '1.25rem',
                    fontFamily: '"Cabinet Grotesk", sans-serif',
                  }}
                >
                  S
                </Box>
              </Box>
              <Box
                sx={{
                  fontFamily: '"Cabinet Grotesk", sans-serif',
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  color: landingColors.textPrimary,
                  letterSpacing: '-0.02em',
                }}
              >
                Store<span style={{ color: landingColors.primary }}>Sphere</span>
              </Box>
            </Box>

            {/* Desktop Navigation */}
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {navItems.map((item) => (
                  <Button
                    key={item.label}
                    onClick={() => scrollToSection(item.href)}
                    sx={{
                      color: landingColors.textSecondary,
                      fontWeight: 500,
                      px: 2,
                      py: 1,
                      borderRadius: '8px',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        color: landingColors.primary,
                        backgroundColor: landingColors.primaryMuted,
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>
            )}

            {/* CTA Buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {!isMobile && (
                <Button
                  variant="text"
                  onClick={() => navigate('/login')}
                  sx={{
                    color: landingColors.textSecondary,
                    fontWeight: 600,
                    '&:hover': {
                      color: landingColors.primary,
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  Sign In
                </Button>
              )}
              <Button
                variant="contained"
                onClick={onRequestDemo}
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  background: `linear-gradient(135deg, ${landingColors.primary} 0%, ${landingColors.primaryDark} 100%)`,
                  boxShadow: '0 4px 14px rgba(99, 102, 241, 0.35)',
                  px: 3,
                  '&:hover': {
                    boxShadow: '0 6px 20px rgba(99, 102, 241, 0.45)',
                  },
                }}
              >
                Request Demo
              </Button>

              {/* Mobile Menu Button */}
              {isMobile && (
                <IconButton
                  onClick={handleDrawerToggle}
                  sx={{
                    color: landingColors.textPrimary,
                    ml: 1,
                  }}
                >
                  <MenuIcon />
                </IconButton>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: 320,
            backgroundColor: landingColors.surface,
            pt: 2,
          },
        }}
      >
        <Box sx={{ px: 2, pb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton onClick={handleDrawerToggle}>
            <CloseIcon />
          </IconButton>
        </Box>
        <List sx={{ px: 2 }}>
          {navItems.map((item) => (
            <ListItem key={item.label} disablePadding>
              <ListItemButton
                onClick={() => scrollToSection(item.href)}
                sx={{
                  borderRadius: '10px',
                  mb: 0.5,
                  '&:hover': {
                    backgroundColor: landingColors.primaryMuted,
                  },
                }}
              >
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: 600,
                    color: landingColors.textPrimary,
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
          <ListItem disablePadding sx={{ mt: 2 }}>
            <ListItemButton
              onClick={() => navigate('/login')}
              sx={{
                borderRadius: '10px',
                border: `2px solid ${landingColors.border}`,
                justifyContent: 'center',
                mb: 1,
              }}
            >
              <ListItemText
                primary="Sign In"
                primaryTypographyProps={{
                  fontWeight: 600,
                  color: landingColors.textPrimary,
                  textAlign: 'center',
                }}
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                handleDrawerToggle();
                onRequestDemo();
              }}
              sx={{
                background: `linear-gradient(135deg, ${landingColors.primary} 0%, ${landingColors.primaryDark} 100%)`,
                py: 1.5,
              }}
            >
              Request Demo
            </Button>
          </ListItem>
        </List>
      </Drawer>

      {/* Toolbar spacer */}
      <Toolbar />
    </>
  );
};

export default Navbar;
